// src/components/TenantHeader.js
//
// Phase 7 — Global "you are here" indicator + tenant/site switcher.
//
// Rendered once in App.js (outside <Routes>), so it appears on every page.
// Beyond navigation, it is a PHI-safety affordance: staff always see which
// company/site's records they're working in.
//
//   - Owner / tenant-admin: company and/or site become dropdowns.
//   - Other roles: read-only label (or a limited dropdown if assigned >1 site).
//   - Hidden on public pages (login, e-sign) and when signed out.

import { useMemo, useRef, useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Building2, MapPin, ChevronDown, Settings, ShieldCheck, Home } from "lucide-react";
import { useTenant } from "../context/TenantContext";
import { UI_REVAMP_ENABLED } from "../config/featureFlags";

const prettyRole = (role, isOwner, isTenantAdmin, tenantId) => {
  if (isOwner) return "Owner";
  if (isTenantAdmin?.(tenantId)) return "Admin";
  if (!role) return "—";
  return role.charAt(0).toUpperCase() + role.slice(1);
};

export default function TenantHeader() {
  const {
    user,
    tenantId,
    siteId,
    isOwner,
    isTenantAdmin,
    roleForActiveSite,
    tenantsMeta,
    sitesMeta,
    canSwitchSite,
    canSwitchTenant,
    switchSite,
    switchTenant,
  } = useTenant();

  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  // Close the panel on outside click.
  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Close on Escape for keyboard users.
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  const tenantLabel = useMemo(() => {
    const t = tenantsMeta.find((x) => x.id === tenantId);
    return t?.displayName || t?.name || tenantId;
  }, [tenantsMeta, tenantId]);

  const siteLabel = useMemo(() => {
    const s = sitesMeta.find((x) => x.id === siteId);
    return s?.name || siteId;
  }, [sitesMeta, siteId]);

  // Hide on public/unauthenticated surfaces.
  const path = location.pathname;
  const hidden =
    !user ||
    path === "/login" ||
    path === "/" ||
    path.startsWith("/esign") ||
    path.startsWith("/referral-form") ||
    path.startsWith("/health-survey");
  if (hidden) return null;

  const showManage = isOwner || isTenantAdmin?.(tenantId);
  const roleLabel = prettyRole(roleForActiveSite, isOwner, isTenantAdmin, tenantId);
  const interactive = canSwitchTenant || canSwitchSite || showManage;
  // UI revamp: a way back to the personal home page, for the roles /home admits.
  // Not on pages whose sidebar has a Home item (Coordinator, Dashboard, Reception).
  // Admins and coordinators only: reception and referral have no home page.
  const showHome =
    UI_REVAMP_ENABLED &&
    !["/home", "/coordinator", "/dashboard", "/reception"].includes(path) &&
    (isOwner || isTenantAdmin?.(tenantId) || ["admin", "coordinator"].includes(roleForActiveSite));

  return (
    <div ref={ref} className="fixed top-3 left-1/2 -translate-x-1/2 z-[60]">
      {showHome && (
        <button
          type="button"
          onClick={() => navigate("/home")}
          className="rx-home-nav absolute right-full top-0 mr-2 inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold"
          title="Your home page"
        >
          <Home className="w-3.5 h-3.5" />
          Home
        </button>
      )}
      <motion.button
        onClick={() => interactive && setOpen((v) => !v)}
        initial={{ opacity: 0, y: -14 }}
        animate={{ opacity: 1, y: 0 }}
        whileHover={{ y: -1 }}
        whileTap={interactive ? { scale: 0.97 } : undefined}
        transition={{ type: "spring", stiffness: 400, damping: 28 }}
        className={`flex items-center gap-2 rounded-full border px-3.5 py-1.5 text-xs text-slate-200 shadow-lg shadow-black/30 transition-colors duration-300 ${
          open
            ? "border-purple-400/40 bg-[#0b0820]/95 ring-1 ring-purple-400/20"
            : "border-white/10 bg-[#0b0820]/80 hover:bg-[#0b0820]/95 hover:border-white/20"
        } backdrop-blur-md ${interactive ? "cursor-pointer" : "cursor-default"}`}
        title="Active company / site"
      >
        <Building2 className="w-3.5 h-3.5 text-purple-300" />
        <span className="font-semibold max-w-[160px] truncate">{tenantLabel}</span>
        <span className="text-slate-600">·</span>
        <MapPin className="w-3.5 h-3.5 text-blue-300" />
        <span className="max-w-[160px] truncate">{siteLabel}</span>
        <span className="ml-1 rounded-full bg-white/[0.06] border border-white/10 px-2 py-0.5 text-[10px] font-medium tracking-wide text-slate-300">
          {roleLabel}
        </span>
        {interactive && (
          <motion.span
            animate={{ rotate: open ? 180 : 0 }}
            transition={{ duration: 0.25, ease: "easeOut" }}
            className="ml-0.5 flex"
          >
            <ChevronDown className="w-3.5 h-3.5 text-slate-400" />
          </motion.span>
        )}
      </motion.button>

      <AnimatePresence>
        {open && interactive && (
          <motion.div
            initial={{ opacity: 0, y: -8, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -8, scale: 0.96 }}
            transition={{ type: "spring", stiffness: 320, damping: 26 }}
            style={{ transformOrigin: "top center" }}
            className="mt-2 w-72 rounded-2xl border border-white/10 bg-[#0b0820]/95 backdrop-blur-xl p-3.5 shadow-2xl shadow-black/50"
          >
            {/* Company */}
            <label className="block text-[10px] uppercase tracking-wider text-slate-400 mb-1.5">
              Company
            </label>
            {canSwitchTenant ? (
              <div className="relative mb-3">
                <select
                  value={tenantId}
                  onChange={(e) => switchTenant(e.target.value)}
                  className="w-full appearance-none rounded-lg bg-white/[0.05] border border-white/10 px-3 py-2 pr-8 text-sm text-slate-100 outline-none transition-colors duration-200 hover:border-white/20 focus:border-purple-400/50 focus:ring-1 focus:ring-purple-400/30"
                >
                  {tenantsMeta.map((t) => (
                    <option key={t.id} value={t.id} className="bg-[#0b0820]">
                      {t.displayName || t.name || t.id}
                    </option>
                  ))}
                </select>
                <ChevronDown className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-500" />
              </div>
            ) : (
              <div className="mb-3 text-sm text-slate-200">{tenantLabel}</div>
            )}

            {/* Site */}
            <label className="block text-[10px] uppercase tracking-wider text-slate-400 mb-1.5">
              Site
            </label>
            {canSwitchSite ? (
              <div className="relative">
                <select
                  value={siteId}
                  onChange={(e) => switchSite(e.target.value)}
                  className="w-full appearance-none rounded-lg bg-white/[0.05] border border-white/10 px-3 py-2 pr-8 text-sm text-slate-100 outline-none transition-colors duration-200 hover:border-white/20 focus:border-blue-400/50 focus:ring-1 focus:ring-blue-400/30"
                >
                  {sitesMeta.map((s) => (
                    <option key={s.id} value={s.id} className="bg-[#0b0820]">
                      {s.name || s.id}
                    </option>
                  ))}
                </select>
                <ChevronDown className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-500" />
              </div>
            ) : (
              <div className="text-sm text-slate-200">{siteLabel}</div>
            )}

            {showManage && (
              <motion.button
                whileHover={{ y: -1 }}
                whileTap={{ scale: 0.97 }}
                transition={{ type: "spring", stiffness: 400, damping: 26 }}
                onClick={() => {
                  setOpen(false);
                  navigate("/manage");
                }}
                className="mt-3.5 w-full flex items-center justify-center gap-2 rounded-lg bg-gradient-to-r from-purple-600 to-blue-600 px-3 py-2 text-sm font-medium text-white shadow-lg shadow-purple-900/30 hover:shadow-purple-700/40 hover:brightness-110 transition-all duration-200"
              >
                {isOwner ? <ShieldCheck className="w-4 h-4" /> : <Settings className="w-4 h-4" />}
                {isOwner ? "Owner console" : "Manage sites & users"}
              </motion.button>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
